// src/mixins/FullscreenMixin.js

export default {
  data() {
    return {
      // 全屏状态
      isFullscreen: false,
      showControls: false,
      prevScrollPosition: null,
      hideTimer: null,

      // iOS 相关状态
      isIOSFullscreen: false,
      iosVolumeChangeHandler: null,
      iosExitFullscreenHandler: null,
      originalVolumeState: null,

      // 标准全屏监听器
      fullscreenChangeHandler: null,

      // 大屏用户信息（私有）
      $_largeScreenUser: null,

      // 视频状态管理
      wasPlayingBeforeFullscreen: false
    };
  },

  computed: {
    isIOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    },
  },

  methods: {
    /**
     * 进入全屏
     */
    enterFullscreen(videoContainer, userId) {
      if (!videoContainer) {
        console.error('未找到视频容器');
        return;
      }

      const video = videoContainer.querySelector('video');
      if (!video) {
        console.error('未找到视频元素');
        return;
      }

      // 保存大屏用户信息
      this.$_largeScreenUser = { userId };

      // 记录进入全屏前的播放状态
      this.wasPlayingBeforeFullscreen = !video.paused && !video.ended;
      console.log('进入全屏前视频状态:', {
        paused: video.paused,
        ended: video.ended,
        wasPlaying: this.wasPlayingBeforeFullscreen
      });

      if (this.isIOS) {
        this.$_enterIOSFullscreen(video, videoContainer);
      } else {
        this.$_enterStandardFullscreen(video);
      }
    },

    /**
     * 退出全屏
     */
    exitFullscreen(videoContainer) {
      if (this.isIOS) {
        this.$_exitIOSFullscreen(videoContainer);
      } else {
        this.$_exitStandardFullscreen();
      }
    },

    /**
     * iOS Safari 进入全屏
     */
    $_enterIOSFullscreen(video, videoContainer) {
      console.log('🚀 进入 iOS Safari 全屏');
      console.log('视频元素:', video);
      console.log('视频容器:', videoContainer);

      this.isIOSFullscreen = true;

      // 监听开始全屏事件
      const handleBeginFullscreen = () => {
        console.log('✅ webkitbeginfullscreen: 开始进入全屏');
      };
      video.addEventListener('webkitbeginfullscreen', handleBeginFullscreen);

      // 监听结束全屏事件
      const handleEndFullscreen = () => {
        console.log('✅ webkitendfullscreen 触发 - 全屏结束');

        // 延迟处理，让系统完成状态切换
        setTimeout(() => {
          this.$_handleIOSExitFullscreen(video);
        }, 100);
      };

      video.addEventListener('webkitendfullscreen', handleEndFullscreen);
      this.iosExitFullscreenHandler = handleEndFullscreen;

      // 设置音量变化监听
      const handleVolumeChange = () => {
        if (this.$_largeScreenUser && this.$_largeScreenUser.userId) {
          const isMuted = video.muted;
          if (this.trtc && this.trtc.muteRemoteAudio) {
            this.trtc.muteRemoteAudio(this.$_largeScreenUser.userId, isMuted);
          }
        }
      };

      this.iosVolumeChangeHandler = handleVolumeChange;
      video.addEventListener('volumechange', handleVolumeChange);

      // 进入全屏
      try {
        console.log('📱 调用 webkitEnterFullscreen()');
        video.webkitEnterFullscreen();
      } catch (error) {
        console.error('iOS进入全屏失败:', error);
        this.$_cleanupIOSListeners(video);
        this.isIOSFullscreen = false;
      }
    },

    /**
     * 处理 iOS 退出全屏
     */
    async $_handleIOSExitFullscreen(video) {
      console.log('🔚 处理 iOS 退出全屏');

      // 如果已经在处理退出流程，直接返回
      if (this._isProcessingIOSExit) return;
      this._isProcessingIOSExit = true;

      try {
        // 处理画中画模式
        if (document.pictureInPictureElement) {
          // 使用一次性监听器
          const handleLeavePictureInPicture = async () => {
            await this._executeIOSExitFullscreen(video);
            video.removeEventListener('leavepictureinpicture', handleLeavePictureInPicture);
          };

          video.addEventListener('leavepictureinpicture', handleLeavePictureInPicture, { once: true });
          return;
        }

        // 普通退出全屏
        await this._executeIOSExitFullscreen(video);
      } catch (error) {
        console.error('处理退出全屏出错:', error);
      } finally {
        this._isProcessingIOSExit = false;
      }
    },

    // 提取公共逻辑到单独的方法
    async _executeIOSExitFullscreen(video) {
      // 清理监听器
      this.$_cleanupIOSListeners(video);

      // 更新状态
      this.isIOSFullscreen = false;

      // 重启视频
      await this.restartRemoteVideo();

      // 触发退出全屏事件
      this.$emit('fullscreen-exit', {
        userId: this.$_largeScreenUser?.userId,
        isIOS: true
      });
    },

    /**
     * 清理iOS监听器
     */
    $_cleanupIOSListeners(video) {
      console.log('🧹 清理iOS监听器');

      // 移除事件监听
      if (video && this.iosExitFullscreenHandler) {
        video.removeEventListener('webkitendfullscreen', this.iosExitFullscreenHandler);
        this.iosExitFullscreenHandler = null;
      }

      if (video && this.iosVolumeChangeHandler) {
        video.removeEventListener('volumechange', this.iosVolumeChangeHandler);
        this.iosVolumeChangeHandler = null;
      }

      // 清理恢复定时器
      if (this.$_videoRestoreTimeout) {
        clearTimeout(this.$_videoRestoreTimeout);
        this.$_videoRestoreTimeout = null;
      }
    },

    /**
     * iOS Safari 退出全屏
     */
    $_exitIOSFullscreen(videoContainer) {
      if (!videoContainer) return;

      const video = videoContainer.querySelector('video');
      if (video && video.webkitExitFullscreen) {
        console.log('📱 手动调用 webkitExitFullscreen');
        video.webkitExitFullscreen();
      }
    },

    /**
     * 标准浏览器进入全屏
     */
    $_enterStandardFullscreen(video) {
      console.log('进入标准浏览器全屏');

      this.prevScrollPosition = {
        x: window.scrollX,
        y: window.scrollY
      };

      document.body.style.overflow = 'hidden';
      this.isFullscreen = true;
      this.showControls = true;

      const element = document.documentElement;

      // 设置全屏变化监听
      const handleFullscreenChange = () => {
        const isFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
        );

        console.log('标准浏览器全屏变化:', { isFullscreen });

        if (!isFullscreen) {
          this.$_handleStandardFullscreenExit();
        }
      };

      const events = [
        'fullscreenchange',
        'webkitfullscreenchange',
        'mozfullscreenchange',
        'MSFullscreenChange'
      ];

      events.forEach(event => {
        document.addEventListener(event, handleFullscreenChange);
      });

      this.fullscreenChangeHandler = handleFullscreenChange;

      // 请求全屏
      const requestMethods = [
        'requestFullscreen',
        'webkitRequestFullscreen',
        'msRequestFullscreen',
        'mozRequestFullScreen'
      ];

      for (const method of requestMethods) {
        if (element[method]) {
          element[method]().catch(err => {
            console.error('全屏请求失败:', err);
            this.$_handleStandardFullscreenExit();
          });
          break;
        }
      }

      this.$_startHideTimer();
    },

    /**
     * 处理标准浏览器退出全屏
     */
    async $_handleStandardFullscreenExit() {
      console.log('退出标准全屏');

      // 清理事件监听器
      if (this.fullscreenChangeHandler) {
        const events = [
          'fullscreenchange',
          'webkitfullscreenchange',
          'mozfullscreenchange',
          'MSFullscreenChange'
        ];

        events.forEach(event => {
          document.removeEventListener(event, this.fullscreenChangeHandler);
        });

        this.fullscreenChangeHandler = null;
      }

      // 恢复状态
      document.body.style.overflow = '';
      this.isFullscreen = false;
      this.showControls = false;

      clearTimeout(this.hideTimer);
      this.hideTimer = null;

      if (this.prevScrollPosition) {
        window.scrollTo(this.prevScrollPosition.x, this.prevScrollPosition.y);
        this.prevScrollPosition = null;
      }

      // 重启视频
      await this.restartRemoteVideo();

      // 触发退出全屏回调
      this.$emit('fullscreen-exit', {
        userId: this.$_largeScreenUser?.userId,
        isIOS: false
      });
    },

    /**
     * 标准浏览器退出全屏
     */
    $_exitStandardFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      }
    },

    /**
     * 开始隐藏控制条计时器
     */
    $_startHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
      }

      this.hideTimer = setTimeout(() => {
        if (this.isFullscreen && this.showControls) {
          this.showControls = false;
        }
      }, 3000);
    }
  },

  beforeDestroy() {
    console.log('🧹 FullscreenMixin beforeDestroy');

    // 清理所有监听器
    if (this.$refs.refLargeScreen) {
      const video = this.$refs.refLargeScreen.querySelector('video');
      if (video) {
        this.$_cleanupIOSListeners(video);
      }
    }

    // 清理标准全屏事件监听器
    if (this.fullscreenChangeHandler) {
      const events = [
        'fullscreenchange',
        'webkitfullscreenchange',
        'mozfullscreenchange',
        'MSFullscreenChange'
      ];

      events.forEach(event => {
        document.removeEventListener(event, this.fullscreenChangeHandler);
      });
    }

    // 清理计时器
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    // 清理恢复定时器
    if (this.$_videoRestoreTimeout) {
      clearTimeout(this.$_videoRestoreTimeout);
      this.$_videoRestoreTimeout = null;
    }
  }
};
